/**
 * Guardian chatbot shell.
 *
 * Composes auth (`useGuardianAuth`), chat runtime (`useChatApp`), and routes
 * the main area between chat, auth pages, incident reporting, and frontier
 * model description screens.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import './App.css';
import GuardianAuth from './components/GuardianAuth';
import GuardianAuthModal from './components/GuardianAuthModal';
import GuardianProfile from './components/GuardianProfile';
import GuardianPricingPage from './components/GuardianPricingPage';
import ReportIncidentForm from './components/ReportIncidentForm';
import MyReportsView from './components/MyReportsView';
import DepartmentView from './components/DepartmentView';
import JoinDepartment from './components/JoinDepartment';
import ShareToDepartmentDialog from './components/ShareToDepartmentDialog';
import TermsConsentDialog from './components/TermsConsentDialog';
import ModelDescriptionPage from './components/ModelDescriptionPage';
import LegalPage from './components/legal/LegalPage';
import ChatWorkspace from './components/chat/ChatWorkspace';
import AppHeader from './components/layout/AppHeader';
import { createInterlinkedTheme } from './theme/interlinkedTheme';
import { getFrontierModel } from './data/frontierModels';
import { useGuardianAuth } from './hooks/useGuardianAuth';
import { useChatApp } from './hooks/useChatApp';
import { usePlainLanguage } from './hooks/usePlainLanguage';
import { useGuardianHistory } from './hooks/useGuardianHistory';
import { historyListToSessions, historyMessagesToThread } from './utils/historyThread';
import { safeStorage } from './utils/safeStorage';
import { clearUserSessions } from './utils/chatSessions';
import { clearGuardianHistoryCache } from './utils/guardianHistoryCache';

function App() {
  const scopeProfile = process.env.REACT_APP_SCOPE_PROFILE || 'guardian';
  const showConnectionDebug = process.env.NODE_ENV === 'development';
  const mainScrollRef = useRef(null);

  const [themeMode, setThemeMode] = useState(() => {
    return safeStorage.get('themeMode') || 'light';
  });

  // Sync `dark` class on <html> for CSS variables outside MUI.
  useEffect(() => {
    const root = document.documentElement;
    if (themeMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [themeMode]);

  const auth = useGuardianAuth(scopeProfile);

  // Plain-language toggle (D2 §3.3): remembered per user, defaulting to the
  // department's plainLanguageDefault for members; sent on every chat message.
  const { plainLanguage, setPlainLanguage } = usePlainLanguage({
    userId: auth.userId,
    authToken: auth.authToken,
    hasDepartment: Boolean(auth.guardianUser?.department),
  });

  // Chat depends on auth; both hooks stay separate to keep concerns isolated.
  const chat = useChatApp({
    userId: auth.userId,
    authToken: auth.authToken,
    guardianUser: auth.guardianUser,
    scopeProfile,
    plainLanguage,
    requiresGuardianAuth: auth.requiresGuardianAuth,
    openAuthModal: auth.openAuthModal,
    // Only the Guardian scope has a sign-in to send the user to (pass 9 F4).
    onUnauthorized: scopeProfile === 'guardian' ? auth.handleUnauthorized : undefined,
    // Consent outstanding (audit P-4): re-read /me, which opens the terms dialog.
    onTermsRequired: scopeProfile === 'guardian' ? auth.refreshProfile : undefined,
  });

  // Server-side conversation history (audit P-12). When the agent supports it,
  // the sidebar lists the user's own conversations and a selected one renders
  // read-only; an older agent (unsupported) keeps the local history unchanged.
  const history = useGuardianHistory({
    userId: auth.userId,
    authToken: auth.authToken,
    wsUrl: chat.backendUrl,
  });
  const { loadConversation: loadHistoryConversation } = history;
  const [viewedThread, setViewedThread] = useState(null);
  // Share-to-department target (WS-F, docs/guardian/06): an assistant answer the
  // user chose to share. Only offered to department members on answers carrying
  // a record id (the Backend verifies the record is the caller's own turn).
  const [shareAnswer, setShareAnswer] = useState(null);

  // The viewed conversation never outlives the login that opened it.
  useEffect(() => {
    setViewedThread(null);
  }, [auth.userId]);

  const historySessions = history.supported
    ? historyListToSessions(history.conversations)
    : null;

  const handleSelectHistory = useCallback(async (id) => {
    const res = await loadHistoryConversation(id);
    if (res.found) {
      setViewedThread({ id, messages: historyMessagesToThread(res.messages) });
    }
  }, [loadHistoryConversation]);

  const handleExitHistory = useCallback(() => setViewedThread(null), []);

  // Start at the top whenever the user navigates to a different page/view.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
  }, [auth.activeView, auth.authPageMode]);

  /** Flips light/dark theme and persists the choice. */
  const toggleTheme = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
    safeStorage.set('themeMode', newMode);
  };

  /**
   * Empty-state chip click: prefill composer and prompt sign-in if needed.
   * @param {string} message - Suggestion text to place in the input.
   */
  const handleSuggestionClick = useCallback((message) => {
    chat.setInputMessage(message);
    auth.requestAuth(() => {});
  }, [chat, auth]);

  const theme = createInterlinkedTheme(themeMode);

  // Props forwarded to ChatInput in both empty and active chat layouts.
  const chatInputProps = {
    value: chat.inputMessage,
    /** Updates the composer text as the user types. */
    onChange: (e) => chat.setInputMessage(e.target.value),
    /** Sends the current composer value to the agent. */
    onSend: () => chat.sendMessage(),
    /** Enter-to-send handler from useChatApp. */
    onKeyDown: chat.handleKeyDown,
    onShareLocation: chat.shareLocation,
    onStartVoiceRecording: chat.startVoiceRecording,
    onStopVoiceRecording: chat.stopVoiceRecording,
    isLocating: chat.isLocating,
    locationLabel: chat.clientLocation?.placeName
      || (chat.locationError ? chat.locationError : null),
    isRecording: chat.isRecording,
    voiceError: chat.voiceError,
    disabled: !chat.isConnected,
    isLoading: chat.isLoading,
    placeholder: 'Ask anything',
    // Plain-language toggle (D2 §3.3): only for the Guardian scope.
    plainLanguage: scopeProfile === 'guardian' ? plainLanguage : undefined,
    onTogglePlainLanguage: scopeProfile === 'guardian' ? setPlainLanguage : undefined,
  };

  const frontierView = getFrontierModel(auth.activeView);
  const pageKey = auth.authPageMode || auth.activeView;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-container il-page-bg">
        <AppHeader
          showConnectionDebug={showConnectionDebug}
          backendUrl={chat.backendUrl}
          showUrlInput={chat.showUrlInput}
          isConnected={chat.isConnected}
          isRetrying={chat.isRetrying}
          connectionError={chat.connectionError || auth.authError}
          authToken={auth.authToken}
          guardianUser={auth.guardianUser}
          promoBadgeVisible={auth.promoBadgeVisible}
          themeMode={themeMode}
          onUrlChange={chat.handleUrlChange}
          onToggleUrlInput={chat.setShowUrlInput}
          onRetryConnection={chat.retryConnection}
          onDismissPromoBadge={auth.dismissPromoBadge}
          onOpenAuthModal={auth.openAuthModal}
          onSignOut={auth.handleSignOut}
          onUserMenuSelect={auth.handleUserMenuSelect}
          onToggleTheme={toggleTheme}
          onHome={auth.goHome}
        />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            minHeight: 0,
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          {auth.authPageMode ? (
            /* Full-page sign-in / sign-up / forgot / reset password. */
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto' }}>
              <GuardianAuth
                initialMode={auth.authPageMode}
                resetToken={auth.passwordResetToken}
                onAuthenticated={auth.handleGuardianAuthenticated}
                onModeChange={(mode) => {
                  // Keep the shell in sync when GuardianAuth switches its internal form.
                  auth.setAuthPageMode(mode);
                  if (mode !== 'reset') {
                    auth.setPasswordResetToken(null);
                  }
                }}
                onCancel={auth.closeAuthPage}
              />
            </Box>
          ) : auth.activeView === 'incident-report' ? (
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <ReportIncidentForm
                authToken={auth.authToken}
                onRequestDraft={chat.requestReportDraft}
                clientLocation={chat.clientLocation}
              />
            </Box>
          ) : auth.activeView === 'my-reports' ? (
            /* Caller's own submitted reports (plan Track 5 B6, 2026-09-30). */
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <MyReportsView authToken={auth.authToken} />
            </Box>
          ) : auth.activeView === 'department' ? (
            /* Department overview + admin (docs/guardian/03 §1.5, 2026-10-21). */
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <DepartmentView
                authToken={auth.authToken}
                currentUserId={auth.guardianUser?.id || null}
                onChanged={auth.refreshProfile}
                onBack={() => auth.setActiveView('chat')}
              />
            </Box>
          ) : auth.activeView === 'join' ? (
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <JoinDepartment
                authToken={auth.authToken}
                onRequestSignIn={auth.openAuthModal}
                onJoined={auth.refreshProfile}
                onDone={() => auth.setActiveView('chat')}
              />
            </Box>
          ) : auth.activeView === 'profile' ? (
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <GuardianProfile
                authToken={auth.authToken}
                user={auth.guardianUser}
                onBack={() => auth.setActiveView('chat')}
                onProfileLoaded={auth.applyProfileFromMe}
                onAccountDeleted={() => {
                  // The account is gone: drop its chats on this device too.
                  clearUserSessions(auth.userId);
                  clearGuardianHistoryCache(auth.userId);
                  auth.handleSignOut();
                }}
              />
            </Box>
          ) : auth.activeView === 'subscriptions' ? (
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <GuardianPricingPage
                authToken={auth.authToken}
                currentTier={
                  auth.entitlements?.plan_slug
                  || auth.entitlements?.tier
                  || auth.guardianUser?.tier
                }
                onBack={() => auth.setActiveView('chat')}
                onProfileLoaded={auth.applyProfileFromMe}
              />
            </Box>
          ) : auth.activeView === 'terms' || auth.activeView === 'privacy' ? (
            /* Public legal documents (audit P-4, 2026-10-22). */
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <LegalPage kind={auth.activeView} onBack={auth.goHome} />
            </Box>
          ) : frontierView && !frontierView.hasLiveAccess ? (
            /* Descriptive landing page for gated frontier models. */
            <Box key={pageKey} ref={mainScrollRef} sx={{ flex: 1, overflow: 'auto' }}>
              <ModelDescriptionPage
                model={frontierView}
                onBack={() => auth.setActiveView('chat')}
              />
            </Box>
          ) : (
            <ChatWorkspace
              key={pageKey}
              sessions={chat.sessions}
              sessionId={chat.sessionId}
              messages={chat.messages}
              sidebarCollapsed={chat.sidebarCollapsed}
              messagesEndRef={chat.messagesEndRef}
              chatInputProps={chatInputProps}
              onToggleSidebar={chat.toggleSidebar}
              onNewChat={chat.handleNewChat}
              onSelectSession={chat.handleSelectSession}
              onDeleteSession={chat.handleDeleteSession}
              onSuggestionClick={handleSuggestionClick}
              onConfirmReport={chat.confirmReport}
              onRate={scopeProfile === 'guardian' ? chat.rateMessage : undefined}
              onShare={
                scopeProfile === 'guardian' && auth.guardianUser?.department
                  ? setShareAnswer
                  : undefined
              }
              historySessions={historySessions}
              viewedThread={viewedThread}
              onSelectHistory={handleSelectHistory}
              onExitHistory={handleExitHistory}
            />
          )}
        </Box>
        {/* Hidden upload <input> removed with the dead /api/upload feature
            (.archive/useChatApp-fileUpload-BAK.js, 2026-09-25 gap closure). */}
      </Box>

      <ShareToDepartmentDialog
        open={Boolean(shareAnswer)}
        authToken={auth.authToken}
        answer={shareAnswer}
        onClose={() => setShareAnswer(null)}
      />

      <TermsConsentDialog
        open={scopeProfile === 'guardian' && auth.consentRequired}
        consent={auth.consent}
        authToken={auth.authToken}
        onAccepted={auth.refreshProfile}
        onSignOut={auth.handleSignOut}
      />

      <GuardianAuthModal
        open={auth.authModalOpen}
        onClose={auth.closeAuthModal}
        onSelectMode={auth.openAuthPage}
      />
    </ThemeProvider>
  );
}

export default App;
